<script setup lang="ts">
    import { useCollapsibleSection } from '~/composables/useCollapsibleSection';

    type CollapseSectionContent = {
        title?: string;
        icon?: string;
        picture?: string;
    };

    type CollapseSectionModifier = 'big-title';

    const props = withDefaults(
        defineProps<{
            content?: CollapseSectionContent;
            defaultOpen?: boolean;
            isExpandable?: boolean;
            modifier?: CollapseSectionModifier[];
            flushX?: boolean;
        }>(),
        {
            content: undefined,
            defaultOpen: true,
            isExpandable: true,
            modifier: () => [],
            flushX: false,
        }
    );

    const emit = defineEmits<{
        (e: 'update:isOpen', value: boolean): void;
    }>();

    const { isOpen, contentRef, contentHeight, toggle } = useCollapsibleSection(props.defaultOpen);

    watch(isOpen, (newValue) => {
        emit('update:isOpen', newValue);
    });
</script>

<template>
    <div class="border-b border-neutral-200 last:border-b-0">
        <button
            v-if="content?.title"
            class="collapse-section-toggle rounded-2xl w-full flex items-center py-4 text-left hover:bg-neutral-50 transition-colors gap-2 pb-4"
            :class="flushX ? 'px-0' : 'px-4'"
            type="button"
            @click="isExpandable ? toggle() : null"
        >
            <template v-if="content?.title">
                <i
                    v-if="content.icon"
                    class="fa-thin text-[24px]"
                    :class="content.icon"
                ></i>
                <img
                    v-else-if="content.picture"
                    :src="content.picture"
                    alt="peppol logo"
                    class="w-[24px] h-[24px] object-contain"
                />
                <h3
                    class="text-gray-900"
                    :class="modifier.includes('big-title') ? $theme('dashboard.boxTitle') : 'font-medium'"
                >
                    {{ content.title }}
                </h3>
            </template>
            <i
                class="fa-solid text-gray-400 transition-transform duration-200 fa-chevron-down ml-auto"
                :class="isExpandable ? (isOpen ? 'rotate-180' : '') : 'hidden'"
            ></i>
        </button>
        <!-- Trigger with a preview -->
        <div v-else>
            <button
                class="collapse-section-toggle rounded-2xl w-full flex items-center p-4 text-left hover:bg-neutral-50 transition-colors gap-2 pb-4"
                type="button"
                @click="isExpandable ? toggle() : null"
            >
                <slot name="title" />
                <i
                    class="fa-solid text-gray-400 transition-transform duration-200 fa-chevron-down ml-auto"
                    :class="isExpandable ? (isOpen ? 'rotate-180' : '') : 'hidden'"
                ></i>
            </button>
            <div class="w-full p-4 pt-0">
                <slot name="preview" />
            </div>
        </div>

        <div
            class="transition-all"
            :class="isOpen ? 'transition-ease-out-quad duration-300' : 'overflow-hidden transition-ease-in-out-quad duration-500'"
            :style="{ height: isOpen ? contentHeight : '0px', opacity: isOpen ? 1 : 0 }"
        >
            <div
                :ref="(el) => (contentRef = el as HTMLElement)"
                class="py-4"
                :class="flushX ? 'px-0' : 'px-4'"
            >
                <slot name="content" />
            </div>
        </div>
    </div>
</template>
